import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../context/SubscriptionContext';
import { Lock, CreditCard, AlertCircle, ArrowRight } from 'lucide-react';

const SubscriptionRestrictedOverlay = () => {
  const navigate = useNavigate();
  const { 
    isRestricted, 
    status, 
    subscriptionTier,
    message 
  } = useSubscription();

  // Don't render if not restricted
  if (!isRestricted) return null;

  const getStatusConfig = () => {
    switch (status) {
      case 'GRACE_EXPIRED':
        return {
          icon: <Lock className="w-12 h-12 text-amber-400" />,
          title: 'Trial Period Ended',
          subtitle: `Your ${subscriptionTier} plan trial has ended`,
          description: 'Subscribe now to continue using all platform features. Your team members are currently unable to log in.',
          buttonText: 'Choose a Plan',
          bgGradient: 'from-amber-500/10 to-orange-500/10'
        };
      case 'SUSPENDED':
        return {
          icon: <AlertCircle className="w-12 h-12 text-red-400" />,
          title: 'Subscription Suspended',
          subtitle: 'Payment issue detected',
          description: 'Your subscription has been suspended due to a payment issue. Please update your payment method to restore access.',
          buttonText: 'Update Payment',
          bgGradient: 'from-red-500/10 to-pink-500/10'
        };
      case 'EXPIRED':
        return {
          icon: <CreditCard className="w-12 h-12 text-gray-400" />,
          title: 'Subscription Expired',
          subtitle: 'Your subscription has ended',
          description: 'Renew your subscription to restore full access to the platform and allow your team members to log in.',
          buttonText: 'Renew Subscription',
          bgGradient: 'from-gray-500/10 to-slate-500/10'
        };
      default:
        return {
          icon: <Lock className="w-12 h-12 text-purple-400" />,
          title: 'Subscription Required',
          subtitle: 'Access restricted',
          description: message || 'Please subscribe to access this feature.',
          buttonText: 'View Plans',
          bgGradient: 'from-purple-500/10 to-indigo-500/10'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div 
      className="fixed inset-0 bg-slate-900/98 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
      data-testid="subscription-restricted-overlay"
    >
      <div className={`bg-gradient-to-br ${config.bgGradient} bg-slate-800 rounded-2xl p-8 max-w-md w-full text-center border border-slate-700/50 shadow-2xl`}>
        {/* Icon */}
        <div className="w-20 h-20 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-6 ring-4 ring-slate-600/30">
          {config.icon}
        </div>
        
        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-2">
          {config.title}
        </h1>
        
        {/* Subtitle */}
        <p className="text-gray-400 text-sm mb-4">
          {config.subtitle}
        </p>
        
        {/* Description */}
        <p className="text-gray-300 mb-8 leading-relaxed">
          {config.description}
        </p>
        
        {/* CTA Button */}
        <button 
          onClick={() => navigate('/admin/plans')}
          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 rounded-xl 
                     font-semibold hover:from-purple-500 hover:to-indigo-500 transition-all duration-200
                     shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40
                     flex items-center justify-center gap-2 group"
          data-testid="view-plans-btn"
        >
          {config.buttonText}
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
        
        {/* Info for team members */}
        {(status === 'GRACE_EXPIRED' || status === 'SUSPENDED' || status === 'EXPIRED') && (
          <div className="mt-6 p-4 bg-slate-700/30 rounded-lg border border-slate-600/50">
            <p className="text-gray-400 text-xs">
              <strong className="text-gray-300">Note:</strong> Your team members are currently unable to access the platform. 
              They will regain access once you complete your subscription setup.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionRestrictedOverlay;
